import React, { FC, useEffect } from 'react';

import Breadcrumb, { BreadcrumbItem } from 'view/section/Breadcrumb/Breadcrumb';
import { QueryParamsFormatter } from 'service/QueryParamsFormatter';
import { useHistory } from 'react-router-dom';
import { Api } from 'service/api/Api';
import { H2, H3 } from 'view/component/Tipografia/Texto';
import { SolicitacoesFiltrosResumo } from 'view/section/SolicitacoesLista/SolicitacoesFiltros/SolicitacoesFiltrosResumo';
import { SolicitacoesFiltrosRapidos } from './SolicitacoesFiltrosRapidos';
import { SolicitacoesFiltros } from './SolicitacoesFiltros';
import { SolicitacoesLista } from 'view/section/SolicitacoesLista/SolicitacoesLista';
import { Conteudo } from 'view/section/Conteudo/Conteudo';
import { useDispatch, useSelector } from 'react-redux';
import { SolicitacoesSelector } from 'store/Solicitacoes/Solicitacoes.selector';
import { SolicitacoesAction } from 'store/Solicitacoes/Solicitacoes.action';
import { OrdemColunaTabela } from 'view/component/Tabela/model/OrdemColunaTabela';
import { SolicitacoesQueryParams } from 'service/api/solicitacao/ApiSolicitacoes';
import { RestricoesCamposBuscaSolicitacoesFormulario } from 'business/formulario/RestricoesBuscaSolicitacoesFormulario';
import { Papel } from 'service/restricao/Papeis';
import { AsyncThunkAction } from '@reduxjs/toolkit';
import { Page } from 'model/Page';
import { SolicitacaoResumida } from 'model/solicitacao/SolicitacaoResumida';
import { useUsuarioLogado } from 'view/component/UsuarioLogadoContext/UsuarioLogadoContext';


interface SolicitacoesPageProps {
  titulo: string|JSX.Element;
  restricoes: RestricoesCamposBuscaSolicitacoesFormulario;
  papel: Papel;
  /**
   * Busca padrão feita quando a página é acessada diretamente pela url sem nenhum filtro padrão
   */
  gerarActionBuscaPadrao: () => AsyncThunkAction<Page<SolicitacaoResumida>, any, {}> | undefined;
  isMinhasSolicitacoes?: boolean;
}


export const SolicitacoesPage: FC<SolicitacoesPageProps> = ({titulo, restricoes, papel, gerarActionBuscaPadrao, isMinhasSolicitacoes}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const usuarioLogado = useUsuarioLogado();

  const filtros = useSelector(SolicitacoesSelector.lista.filtros);
  const ordem = useSelector(SolicitacoesSelector.lista.ordem);
  const isOcioso = useSelector(SolicitacoesSelector.lista.isOcioso);

  const buscar = (filtros?: SolicitacoesQueryParams, ordem?: OrdemColunaTabela) => dispatch(SolicitacoesAction.lista.iniciar(filtros, ordem));
  const alterarOrdem = (ordem?: OrdemColunaTabela) => dispatch(SolicitacoesAction.lista.alterarOrdem(ordem));

  const hasQueryParamDefinida = history.location?.search !== "";
  const {filtros: filtrosIniciais, ordem: ordemInicial} = QueryParamsFormatter.decodeWithOrder(history.location?.search);

  useEffect(() => {
    if (isOcioso) {
      if (hasQueryParamDefinida) {
        dispatch(SolicitacoesAction.lista.iniciar(filtrosIniciais, ordemInicial));
      } else {
        const action = gerarActionBuscaPadrao();
        if (action !== undefined) {
          dispatch(action);
        }
      }
    }
  }, [dispatch, isOcioso, hasQueryParamDefinida, filtrosIniciais, ordemInicial, gerarActionBuscaPadrao]);

  const atualizarEndereco = (filtrosAtualizados: SolicitacoesQueryParams, ordemAtualizada?: OrdemColunaTabela) => {
    history.push({
      search: `${QueryParamsFormatter.format(filtrosAtualizados)}&${Api.ordemToApi(ordemAtualizada)}`
    });
  }

  const onFiltrosAtualizados = (filtros: SolicitacoesQueryParams) => {
    let filtrosAplicar = filtros;
    //Caso seja a página de Minhas Solicitações, o filtro sempre irá conter o id do usuário
    if (isMinhasSolicitacoes){
      filtrosAplicar = {...filtrosAplicar, idSolicitante: [usuarioLogado.idInstitucional] }
    }
    buscar(filtrosAplicar, undefined);
    atualizarEndereco(filtrosAplicar, ordem);
  }

  const onOrdemAtualizada = (ordemAtualizada?: OrdemColunaTabela) => {
    alterarOrdem(ordemAtualizada);
    atualizarEndereco(filtros, ordemAtualizada);
  }

  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem href="/solicitacoes">Solicitações</BreadcrumbItem>
        <BreadcrumbItem>{titulo}</BreadcrumbItem>
      </Breadcrumb>

      <Conteudo className="solicitacoes-page">
        <header>
          <H2>{titulo}</H2>
        </header>
        
        <section>
          <SolicitacoesFiltros restricoes={restricoes} filtros={filtros} onFiltrosAtualizados={onFiltrosAtualizados} />
          <H3>Solicitações</H3>

          {/* Os filtros rápidos e o resumo não são apresentados na página de Minhas Solicitações */}
          {!isMinhasSolicitacoes && <SolicitacoesFiltrosRapidos papel={papel} onFiltrosAtualizados={onFiltrosAtualizados} />}
          {!isMinhasSolicitacoes && <SolicitacoesFiltrosResumo filtros={filtros} ordem={ordem} onFiltrosAtualizados={onFiltrosAtualizados} onOrdemAtualizada={onOrdemAtualizada} />}

          <SolicitacoesLista onChangeOrdem={onOrdemAtualizada} />
        </section>
      </Conteudo>
    </>
  )
}
